import React, { useState } from "react";
import cropr, { GeometryModel, Model, Storage, StorageAddress } from "../../shared/utils/cropr";
import { gettext } from "../../shared/utils/legacy";
import { Modal } from "react-bootstrap";
import GeometryModelPreview from "../../shared/components/GeometryModelPreview";
import LabeledField from "../../shared/forms/LabeledField";
import ModalHeader from "../../shared/components/ModalHeader";
import GeneralError from "../../shared/forms/GeneralError";
import ModelEditModalFooter from "../../shared/components/ModelEditModalFooter";
import LabeledSelect from "../../shared/forms/LabeledSelect";
import { ModelSubmitFormik, PropertiesModalProps } from "../../shared/utils/modals";

type IdKey = [number, { name: string }];

const StoragePropertiesModal = ({
  model,
  onSave,
  onHide,
  requiredSubAttrs,
}: PropertiesModalProps<GeometryModel>) => {
  const [show, setShow] = useState(true);
  const typeName = gettext("storage");
  function hide() {
    setShow(false);
    onHide?.();
  }

  const types = ["storage_types", "storage_bottom_types"].map((key) =>
    // @ts-ignore
    Object.entries(cropr.data[key]).map(([value, { name: label }]: IdKey) => ({
      value: String(value),
      label,
    }))
  );

  const existingAddress = model.get("address") as StorageAddress | null;
  const initialValues: Storage = {
    name: model.get("name") || Model.Storage.generateName(),
    registered_area: model.get("new_area") || model.get("registered_area") || "",
    type: model.get("type") || requiredSubAttrs?.type,
    bottom_type: model.get("bottom_type") || requiredSubAttrs?.["bottom_type"] || "",
    geometry: model.get("geometry") || model.get("addshape_geometry"),
    address: {
      line_1: existingAddress?.line_1 || "",
      city: existingAddress?.city || "",
      zipcode: existingAddress?.zipcode || "",
      country: existingAddress?.country || "",
    },
  };
  const props = {
    hide,
    typeName,
    model,
    collection: cropr.storages,
    permission: cropr.permissions.storage,
  };
  function beforeSave(values: any) {
    if (values.registered_area === "") values.registered_area = null;
    return values;
  }
  return (
    <Modal className="modal-md dfi-modal" show={show} onHide={hide} backdrop="static">
      <ModelSubmitFormik
        initialValues={initialValues}
        onSave={onSave}
        {...props}
        beforeSave={beforeSave}
      >
        {({ values, handleSubmit, isSubmitting, setFieldValue, errors }) => (
          <form onSubmit={handleSubmit}>
            <ModalHeader
              title={model.id ? gettext("Edit storage properties") : gettext("Create storage")}
            />
            <Modal.Body>
              <GeneralError errors={errors} />
              <div className="row">
                <div className="col-4">
                  <GeometryModelPreview model={model} />
                </div>
                <div className="col-8">
                  <LabeledField
                    label={gettext("Name")}
                    name="name"
                    type="text"
                    placeholder={gettext("Enter a storagename")}
                  />
                  <LabeledField
                    label={`${gettext("Area")} ${cropr.l10n.unit_area_display}`}
                    name="registered_area"
                    placeholder={gettext("Area")}
                    type="text"
                  />
                  <LabeledSelect
                    label={gettext("Storage type")}
                    menuPlacement="top"
                    options={types[0]}
                    name="type"
                  />
                  {values["type"] === "outdoor" && (
                    <LabeledSelect
                      label={gettext("Bottom type")}
                      menuPlacement="top"
                      options={types[1]}
                      name="bottom_type"
                    />
                  )}
                  <hr className="my-3" />
                  <h6 className="mb-3">{gettext("Address")}</h6>
                  <LabeledField
                    label={gettext("Street and number")}
                    name="address.line_1"
                    type="text"
                    placeholder={gettext("Enter street and number")}
                  />
                  <div className="row">
                    <div className="col-4">
                      <LabeledField
                        label={gettext("Zipcode")}
                        name="address.zipcode"
                        type="text"
                        placeholder={gettext("Zipcode")}
                      />
                    </div>
                    <div className="col-8">
                      <LabeledField
                        label={gettext("City")}
                        name="address.city"
                        type="text"
                        placeholder={gettext("City")}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Modal.Body>
            <ModelEditModalFooter disabled={isSubmitting} {...props} />
          </form>
        )}
      </ModelSubmitFormik>
    </Modal>
  );
};

export default StoragePropertiesModal;
